import { EditableDate } from '../model/interfaces/editable-date.interface';
import { PayoffScheduleObservation } from '../model/interfaces/payoff-schedule-observation.interface';
import { PayoffSchedule } from '../model/interfaces/payoff-schedule.interface';
import { GetDateObjectFromString } from './get-date-object-from-string';

function GetPayoffScheduleEditableDate(
  date: EditableDate
): EditableDate | null {
  if (!date) {
    return null;
  }

  return {
    original: GetDateObjectFromString(date.original as string),
    edited: date.edited ? GetDateObjectFromString(date.edited as string) : null,
  };
}

const getDateListAsEditable = (schedule: (string | EditableDate)[]) => {
  return (
    (schedule?.map((item) =>
      GetPayoffScheduleEditableDate(
        typeof item === 'string' ? { original: item, edited: null } : item
      )
    ) as EditableDate[]) || []
  );
};

export function MapPayoffScheduleToDate(
  schedule: PayoffSchedule[]
): PayoffSchedule[] {
  if (!schedule) {
    return [];
  }

  const newSchedule = JSON.parse(JSON.stringify(schedule));
  const out = newSchedule.map((item: PayoffSchedule) => {
    return {
      ...item,
      unadjustedPaymentDate: GetPayoffDateFromStringOrObject(
        item.unadjustedPaymentDate
      ),
      adjustedPaymentDate: GetPayoffDateFromStringOrObject(
        item.adjustedPaymentDate
      ),
      accrualPeriodStart: GetPayoffDateFromStringOrObject(
        item.accrualPeriodStart
      ),
      accrualPeriodEnd: GetPayoffDateFromStringOrObject(item.accrualPeriodEnd),
      rateDeterminationDate: GetPayoffDateFromStringOrObject(
        item.rateDeterminationDate ||
          item.observations?.[0]?.rateDeterminationDate
      ),
      callNoticeDate: GetPayoffDateFromStringOrObject(item.callNoticeDate),
      observations: item.observations?.length
        ? item.observations.map((obs: PayoffScheduleObservation) => {
            return {
              ...obs,
              rateDeterminationDate: GetPayoffDateFromStringOrObject(
                obs.rateDeterminationDate
              ),
              rateLastResetDateForFixing: GetPayoffDateFromStringOrObject(
                obs.rateLastResetDateForFixing
              ),
              rateDeterminationDates: getDateListAsEditable(
                obs.rateDeterminationDates
              ),
              rateCalculationDates: getDateListAsEditable(
                obs.rateCalculationDates
              ),
              rateResetDates: getDateListAsEditable(obs.rateResetDates),
              rateResetDatesDetail: obs.rateResetDatesDetail?.map((detail) => {
                return detail
                  ? {
                      resetDate: GetPayoffDateFromStringOrObject(
                        detail.resetDate
                      ),
                      resetDateAfterLookback: GetPayoffDateFromStringOrObject(
                        detail.resetDateAfterLookback
                      ),
                    }
                  : null;
              }),
              unadjustedObsStartDate: GetPayoffDateFromStringOrObject(
                obs.unadjustedObsStartDate
              ),
              unadjustedObsEndDate: GetPayoffDateFromStringOrObject(
                obs.unadjustedObsEndDate
              ),
              adjustedObsStartDate: GetPayoffDateFromStringOrObject(
                obs.adjustedObsStartDate
              ),
              adjustedObsEndDate: GetPayoffDateFromStringOrObject(
                obs.adjustedObsEndDate
              ),
              unadjustedBarrierObsDates: getDateListAsEditable(
                obs.unadjustedBarrierObsDates
              ),
              adjustedBarrierObsDates: getDateListAsEditable(
                obs.adjustedBarrierObsDates
              ),
              unadjustedBarrierFixingDates: getDateListAsEditable(
                obs.unadjustedBarrierFixingDates
              ),
              adjustedBarrierFixingDates: getDateListAsEditable(
                obs.adjustedBarrierFixingDates
              ),
              unadjustedInitialFixingDates: getDateListAsEditable(
                obs.unadjustedInitialFixingDates
              ),
              adjustedInitialFixingDates: getDateListAsEditable(
                obs.adjustedInitialFixingDates
              ),
              unadjustedFinalFixingDates: getDateListAsEditable(
                obs.unadjustedFinalFixingDates
              ),
              adjustedFinalFixingDates: getDateListAsEditable(
                obs.adjustedFinalFixingDates
              ),
            };
          })
        : [],
    };
  });
  return out;
}

function GetPayoffDateFromStringOrObject(
  date: string | EditableDate
): EditableDate {
  return typeof date === 'string'
    ? {
        original: GetDateObjectFromString(date),
        edited: null,
      }
    : GetPayoffScheduleEditableDate(date as EditableDate);
}
